"use client";

import { useState, useRef, useCallback } from "react";
import {
  Type,
  Square,
  Circle,
  Triangle,
  Minus,
  Image as ImageIcon,
  Trash2,
  Save,
  Palette,
  Grid3X3,
  LayoutGrid,
  Table,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";

interface TemplateToolbarProps {
  onAddText: () => void;
  onAddShape: (type: "rect" | "circle" | "triangle" | "line") => void;
  onAddImage: (url: string) => void;
  onAddTable: (rows: number, cols: number) => void;
  onAddPlaceholderZone: (type: "pricing_grid" | "output_value" | "input_value" | "data_table") => void;
  onDelete: () => void;
  onSetBackground: (color: string) => void;
  onSave: () => void;
  hasSelection: boolean;
  isSaving?: boolean;
  hasUnsavedChanges?: boolean;
  readOnly?: boolean; // When true, disable all editing actions
  isInheritedPage?: boolean; // When true, show inherited page indicator (tools still enabled)
}

const MAX_GRID_ROWS = 8;
const MAX_GRID_COLS = 8;

// Palette de couleurs Logissimo pour les fonds
const PRESET_COLORS = [
  "#ffffff",
  "#f8f9fa",
  "#e9ecef",
  "#FFF5CC",
  "#CCEFED",
  "#CCE0F0",
  "#FACCE6",
  "#FFCC00",
  "#00B2A9",
  "#003366",
];

export function TemplateToolbar({
  onAddText,
  onAddShape,
  onAddImage,
  onAddTable,
  onAddPlaceholderZone,
  onDelete,
  onSetBackground,
  onSave,
  hasSelection,
  isSaving = false,
  hasUnsavedChanges = false,
  readOnly = false,
  isInheritedPage = false,
}: TemplateToolbarProps) {
  const [imageUrl, setImageUrl] = useState("");
  const [customColor, setCustomColor] = useState("#ffffff");
  const [hoveredCell, setHoveredCell] = useState<{ row: number; col: number } | null>(null);
  const [tablePopoverOpen, setTablePopoverOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleGridSelect = useCallback((rows: number, cols: number) => {
    onAddTable(rows, cols);
    setTablePopoverOpen(false);
    setHoveredCell(null);
  }, [onAddTable]);

  const handleImageUrlSubmit = () => {
    if (imageUrl.trim()) {
      onAddImage(imageUrl.trim());
      setImageUrl("");
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        onAddImage(dataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="flex items-center gap-1 p-2 bg-muted/50 border-b flex-wrap">
      {/* Read-only indicator */}
      {readOnly && !isInheritedPage && (
        <span className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded border border-blue-200 mr-2">
          Lecture seule
        </span>
      )}
      {/* Inherited page indicator - tools remain enabled for adding child elements */}
      {isInheritedPage && (
        <span className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded border border-blue-200 mr-2">
          Page heritee - elements parents verrouilles
        </span>
      )}

      {/* Text Tool */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onAddText}
        title="Ajouter du texte"
        disabled={readOnly}
      >
        <Type className="h-4 w-4" />
      </Button>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Shape Tools */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" title="Ajouter une forme" disabled={readOnly}>
            <Square className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onClick={() => onAddShape("rect")} disabled={readOnly}>
            <Square className="h-4 w-4 mr-2" />
            Rectangle
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onAddShape("circle")} disabled={readOnly}>
            <Circle className="h-4 w-4 mr-2" />
            Cercle
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onAddShape("triangle")} disabled={readOnly}>
            <Triangle className="h-4 w-4 mr-2" />
            Triangle
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onAddShape("line")} disabled={readOnly}>
            <Minus className="h-4 w-4 mr-2" />
            Ligne
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Image Tool */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" title="Ajouter une image" disabled={readOnly}>
            <ImageIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">URL de l&apos;image</label>
              <div className="flex gap-2 mt-1">
                <Input
                  placeholder="https://..."
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleImageUrlSubmit();
                  }}
                  disabled={readOnly}
                />
                <Button size="sm" onClick={handleImageUrlSubmit} disabled={readOnly}>
                  Ajouter
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-popover px-2 text-muted-foreground">ou</span>
              </div>
            </div>
            <div>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*"
                onChange={handleFileSelect}
                disabled={readOnly}
              />
              <Button
                variant="outline"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
                disabled={readOnly}
              >
                <ImageIcon className="h-4 w-4 mr-2" />
                Choisir un fichier
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Table Tool */}
      <Popover open={tablePopoverOpen} onOpenChange={setTablePopoverOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" title="Ajouter un tableau" disabled={readOnly}>
            <Table className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="start">
          <div className="space-y-2">
            <div className="text-sm font-medium">Inserer un tableau</div>
            <div
              className="grid gap-0.5"
              style={{ gridTemplateColumns: `repeat(${MAX_GRID_COLS}, 1fr)` }}
              onMouseLeave={() => setHoveredCell(null)}
            >
              {Array.from({ length: MAX_GRID_ROWS }, (_, row) =>
                Array.from({ length: MAX_GRID_COLS }, (_, col) => {
                  const isHighlighted =
                    hoveredCell !== null &&
                    row <= hoveredCell.row &&
                    col <= hoveredCell.col;
                  return (
                    <button
                      key={`${row}-${col}`}
                      className={`w-5 h-5 border rounded-sm transition-colors ${
                        isHighlighted
                          ? "bg-primary/30 border-primary"
                          : "bg-background border-border hover:border-primary/50"
                      }`}
                      onMouseEnter={() => setHoveredCell({ row, col })}
                      onClick={() => handleGridSelect(row + 1, col + 1)}
                      disabled={readOnly}
                    />
                  );
                })
              )}
            </div>
            {hoveredCell && (
              <div className="text-xs text-muted-foreground text-center">
                {hoveredCell.row + 1} x {hoveredCell.col + 1}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Placeholder Zone Tools */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" title="Ajouter une zone placeholder" className="text-primary" disabled={readOnly}>
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onClick={() => onAddPlaceholderZone("pricing_grid")} disabled={readOnly}>
            <Grid3X3 className="h-4 w-4 mr-2" />
            Zone Grille Tarifaire
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onAddPlaceholderZone("output_value")} disabled={readOnly}>
            <Type className="h-4 w-4 mr-2" />
            Zone Valeur Output
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onAddPlaceholderZone("input_value")} disabled={readOnly}>
            <Type className="h-4 w-4 mr-2" />
            Zone Valeur Input
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onAddPlaceholderZone("data_table")} disabled={readOnly}>
            <Table className="h-4 w-4 mr-2" />
            Zone Tableau de donnees
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Background Color */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" title="Couleur de fond" disabled={readOnly}>
            <Palette className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64">
          <div className="space-y-3">
            <label className="text-sm font-medium">Couleur de fond</label>
            <div className="grid grid-cols-5 gap-2">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  className="w-8 h-8 rounded border border-border hover:scale-110 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: color }}
                  onClick={() => onSetBackground(color)}
                  title={color}
                  disabled={readOnly}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                type="color"
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value)}
                className="w-12 h-8 p-0 border-0"
                disabled={readOnly}
              />
              <Input
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value)}
                placeholder="#ffffff"
                className="flex-1"
                disabled={readOnly}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => onSetBackground(customColor)}
                disabled={readOnly}
              >
                Appliquer
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Delete */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onDelete}
        disabled={!hasSelection || readOnly}
        title="Supprimer la selection"
        className="text-destructive hover:text-destructive"
      >
        <Trash2 className="h-4 w-4" />
      </Button>

      <div className="flex-1" />

      {/* Save */}
      <Button
        variant={hasUnsavedChanges ? "default" : "outline"}
        size="sm"
        onClick={onSave}
        disabled={isSaving}
      >
        <Save className="h-4 w-4 mr-2" />
        {isSaving ? "Enregistrement..." : "Enregistrer"}
      </Button>
    </div>
  );
}
